// Site-wide metadata. Page copy lives in the page components (src/pages/*),
// which are 1:1 ports of the Webflow markup; blog posts live in posts.json.
import { findPost, posts } from "./posts";

const foundingYear = 2008;

export const site = {
  name: "Abilitics",
  /** Canonical origin — www.abilitics.com 301s here (Cloudflare redirect rule). */
  url: "https://abilitics.com",
  foundingYear,
  /** Used in the copy ("N years on the market"); the build bakes in the current year. */
  yearsOnMarket: new Date().getFullYear() - foundingYear,
  /** Webflow site id — the IX2 runtime in public/webflow/webflow.<hash>.js is keyed to it. */
  wfSiteId: "655c5d4d25d668c29fb0f172",
  /** Default share image (1200×630 JPG — some networks reject WebP). */
  ogImage: "/og-image.jpg",
};

export interface PageMeta {
  /** Webflow page id. IX2 interactions and Lottie triggers only run for the matching page id. */
  wfPageId: string;
  title: string;
  description: string;
}

export const pages: Record<string, PageMeta> = {
  "/": {
    wfPageId: "65606fe9d2b91ed699fbaaff",
    title: "Abilitics | AI Venture Studio for HR, Sales & Communities",
    description: `Abilitics is a venture studio building AI ventures in HR, Sales and Communities, backed by ${site.yearsOnMarket} years of software development expertise.`,
  },
  "/for-founders": {
    wfPageId: "65606fe9d2b91ed699fbab23",
    title: "For Founders | Abilitics Venture Studio",
    description:
      "Partner with Abilitics to build your startup: a proven venture-building process, MVPs and ongoing R&D, and initial funding with access to investors.",
  },
  "/ventures": {
    wfPageId: "66bb29acb777b05359985356",
    title: "Our Ventures | Abilitics",
    description:
      "Meet the Abilitics ventures in AI, Communities & Events, Sales and HR: Skillie, VibeJobs, Relinkd, Kognify, Immersica and VR Express.",
  },
  "/about-us": {
    wfPageId: "6560924fa2da9316e57488e7",
    title: "About Us | Abilitics",
    description:
      "Founded in 2008, Abilitics is a venture studio in South-Eastern Europe that believes AI can better humanity. Meet our values, mission and leadership team.",
  },
  "/blog": {
    wfPageId: "65606fe9d2b91ed699fbab0e",
    title: "Blog | Abilitics",
    description:
      "News from Abilitics and our ventures, plus customer stories from Coca-Cola HBC, Postbank, LVMH, WWF and more.",
  },
  "/contact": {
    wfPageId: "65606fe9d2b91ed699fbab12",
    title: "Contact | Abilitics",
    description:
      "Want to partner with Abilitics or one of our ventures? Write to our leadership team by email, on LinkedIn or through our contact form.",
  },
  "/privacy-policy": {
    // Blog-post template id: the page reuses that markup and its heading fade-in.
    wfPageId: "65606fe9d2b91ed699fbab17",
    title: "Privacy Policy | Abilitics",
    description:
      "How Abilitics handles personal data on abilitics.com: no cookies or tracking, what the contact form collects, our service providers and your GDPR rights.",
  },
};

export const blogPostWfPageId = "65606fe9d2b91ed699fbab17";
export const notFoundWfPageId = "65606fe9d2b91ed699fbab0c";

export const notFound: PageMeta = {
  wfPageId: notFoundWfPageId,
  title: "Not Found | Abilitics",
  description: "The page you are looking for doesn't exist or has been moved",
};

/** Meta for a URL path; `notFound` for anything that isn't a page or a known blog post. */
export function metaFor(pathname: string): PageMeta {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  if (pages[path]) return pages[path];
  const post = path.startsWith("/news/") ? findPost(path.slice("/news/".length)) : undefined;
  if (post) return { wfPageId: blogPostWfPageId, title: `${post.title} | Abilitics`, description: post.description || post.subtitle || post.title };
  return notFound;
}

/** Every indexable URL path — prerendered to static HTML and listed in sitemap.xml. */
export const routes = [...Object.keys(pages), ...posts.map((p) => `/news/${p.slug}`)];
